/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import BaseInput from '@ba-ui-toolkit/baseinput';
import styles from './TextInput.scss';
import stringResource from './nls/StringResource';

const { sanitize, getInheritedProps } = propsUtils;

const _CLASS = styles.textInputClassName.replace(/"/g, '');

export default class TextInput extends Component {
	static propTypes = {
		...BaseInput.propTypes,

		// these props have a different definition then the baseInput props
		type: PropTypes.oneOf(['password']),
		value: PropTypes.oneOfType([PropTypes.string]),

		// these props are in addition to the baseInput props
		inputChildren: PropTypes.node,
		allowEmpty: PropTypes.bool,
	};

	static defaultProps = {
		className: '',
		style: {},
		allowEmpty: true,
	};

	constructor(props) {
		super(props);
		this.state = {
			displayValue: undefined,
		};
	}

	// eventually/hopefully onValueChange/onValueAccept will be the norm, and useOnValueCallbacks can be removed
	_useOnValueCallbacks = !!this.props.onValueChange || !!this.props.onValueAccept;

	// accept only a valid string as input
	_fixupValue = value => {
		const { type } = this.props;

		if (type === 'password' || value === undefined) {
			return undefined;
		}
		if (typeof value === 'number') {
			return String(value);
		}
		if (typeof value === 'string') {
			return value;
		}

		return '';
	};

	componentDidMount() {
		const { value } = this.props;

		const fixedNewValue = this._fixupValue(value);
		const { rawValue, infoMessage } = this._valueInfo(fixedNewValue);

		this.setState({
			displayValue: rawValue,
			infoMessage,
		});
	}

	componentWillReceiveProps({ value: newValue }) {
		const { value: oldValue } = this.props;
		const fixedOldValue = this._fixupValue(oldValue);
		const fixedNewValue = this._fixupValue(newValue);

		if (fixedNewValue !== fixedOldValue) {
			const { rawValue, infoMessage } = this._valueInfo(fixedNewValue);

			this.setState({
				displayValue: rawValue,
				infoMessage,
			});
		}
	}

	focus() {
		const { disabled } = this.props;

		!disabled && this._input && this._input.focus();
	}

	_ref = input => {
		this._input = input;
	};

	_valueBounded = value => {
		const valueBounded = {
			boundValue: value,
		};

		// other error handling issue could be min/max/trimStart/trimEnd
		// this._valueFixUpMin(valueBounded);
		// this._valueFixUpMax(valueBounded);
		// this._valueFixUpTrimStart(valueBounded);
		// this._valueFixUpTrimEnd(valueBounded);

		return valueBounded;
	};

	_valueInfo = value => {
		const { allowEmpty } = this.props;

		// if value is blank/empty
		if (!value) {
			return {
				rawValue: value,
				boundValue: !allowEmpty ? undefined : value,
				infoMessage: !allowEmpty ? stringResource.get('empty') : undefined,
			};
		}

		return {
			rawValue: value,
			...this._valueBounded(value),
		};
	};

	_onValueChange = newValue => {
		const { onValueChange } = this.props;
		const { boundValue, rawValue, infoMessage } = this._valueInfo(newValue);

		this.setState({
			displayValue: rawValue,
			infoMessage,
		});

		// if there is additional processing, call it
		onValueChange && onValueChange(boundValue, rawValue);
	};

	_onChange = (newValue, e) => {
		const { onChange } = this.props;

		this._onValueChange(newValue);
		onChange && onChange(newValue, e);
	};

	_onValueAccept = newValue => {
		const { value: oldValue, onValueAccept } = this.props;
		const { boundValue: newBoundValue, rawValue: newRawValue } = this._valueInfo(newValue);
		const { rawValue: oldRawValue, infoMessage: oldInfoMessage } = this._valueInfo(
			this._fixupValue(oldValue)
		);

		this.setState({
			displayValue: oldRawValue,
			infoMessage: oldInfoMessage,
		});

		// check if the old value does not match the new bound value
		if (oldRawValue !== newBoundValue) {
			// if there is additional processing, call it
			onValueAccept && onValueAccept(newBoundValue, newRawValue);
		}
	};

	render() {
		// extract props that are to be manipulated or not passed down
		const {
			// textInput specific
			inputChildren,
			allowEmpty,
			// inherited props
			dir,
			textDir,
			className,
			validationErrorText,
			hasValidationError,
			onChange,
			// get all the other props
			...others
		} = this.props;
		const { displayValue, infoMessage } = this.state;

		const classNames = cx(`${_CLASS}`);
		const localMessage = this._useOnValueCallbacks && infoMessage ? infoMessage : undefined;

		return (
			<BaseInput
				className={`ba-common-text-input ${className} ${classNames}`}
				data-tid="ba-common-text-input"
				{...sanitize(others, TextInput)}
				{...getInheritedProps(others, BaseInput)}
				ref={this._ref}
				{...(dir ? { dir } : {})}
				{...(textDir ? { textDir } : {})}
				value={displayValue}
				children={inputChildren}
				onChange={this._useOnValueCallbacks ? onChange : this._onChange}
				onValueChange={this._useOnValueCallbacks ? this._onValueChange : undefined}
				onValueAccept={this._useOnValueCallbacks ? this._onValueAccept : undefined}
				validationErrorText={localMessage || validationErrorText}
				hasValidationError={localMessage ? true : hasValidationError}
			/>
		);
	}
}
